import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { StyleSheet, ScrollView, View, ActivityIndicator, Pressable, Platform, RefreshControl, KeyboardAvoidingView, Keyboard } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import StatusContainer from '@/components/StatusContainer';
import Icon from '@/components/Icon';
import ToolForm from '@/components/Tools/ToolForm';
import ToolResultDisplay from '@/components/Tools/ToolResultDisplay';
import ToolProgressIndicator from '@/components/Tools/ToolProgressIndicator';
import ToolEmptyResult from '@/components/Tools/ToolEmptyResult';
import ToolRecentRunsStrip from '@/components/Tools/ToolRecentRunsStrip';
import ToolRelatedTools from '@/components/Tools/ToolRelatedTools';
import ToolRelatedPrompts from '@/components/Tools/ToolRelatedPrompts';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import AppDashboardFooterBottomBar from '@/components/AppDashboardFooterBottomBar';
import ToolDetailHeader from '@/components/Tools/ToolDetailHeader';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import CatalogDetailPageSkeleton from '@/components/Catalog/CatalogDetailPageSkeleton';
import ToolRunButton from '@/components/Tools/ToolRunButton';
import {
  getPaletteForTool,
  getRunStatusVisual,
} from '@/components/Tools/toolDetailHelpers';
import { useToolStore, useToastStore, useAuthStore, useModelStore, useModalStore } from '@/store';
import { toolRunsApi, toolsApi } from '@/lib/api';
import { useModalHelper } from '@/hooks/useModalHelper';
import useRequireAuth from '@/hooks/useRequireAuth';
import { isPlusRequiredApiError, openModelAccessPaywall } from '@/constants/utils/trial';
import { isInsufficientCreditsError } from '@/lib/plusCreditOptions';
import { formatCreditBalance } from '@/lib/formatCredits';

// Header (sidebar + back + presets / runs): `getToolDetailRouteNavigationOptions`
// in `ToolDetailRouteNavigationOptions.jsx`.

// Convenience: anything that's a real touch device. Drives all native-only
// affordances we layer on this page (sticky run CTA,
// keyboard avoidance, etc).
const IS_TOUCH_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';

// ---------------------------------------------------------------------------
// SEO — static generation
// ---------------------------------------------------------------------------
// Build-time list of slugs to prerender + a side cache populated with the
// fully-detailed tool payload so the page can render proper meta tags into
// the static HTML even before the client fetches the live tool.
const _staticToolCache = {};
export async function generateStaticParams() {
  // Rendered on demand (backend SSR meta + client SPA), not prerendered.
  // Returning [] keeps `expo export` fast and avoids hammering the API at
  // build time. SEO meta is injected at request time; see backend appRoutes.
  return [];
}

// Module-level dedupe for view beacons so revisiting the same tool inside one
// session (back/forward, hot reload, etc.) doesn't repeatedly inflate stats.
const VIEWED_TOOL_SLUGS = new Set();

// Stable empty array used as a fallback inside Zustand selectors so the
// snapshot doesn't change on every render (would otherwise trigger an
// infinite "Maximum update depth exceeded" loop via useSyncExternalStore).
const EMPTY_RUNS = [];

const HOW_IT_WORKS_STEPS = [
  { icon: 'sliders',             titleKey: 'pages.tools.howStepConfigureTitle', titleFallback: 'Configure',        copyKey: 'pages.tools.howStepConfigureCopy', copyFallback: 'Fill in the inputs — try a preset to see how it works.' },
  { icon: 'wand-magic-sparkles', titleKey: 'pages.tools.howStepRunTitle',       titleFallback: 'Run',              copyKey: 'pages.tools.howStepRunCopy',       copyFallback: "Tap Run. We'll handle the model selection and processing." },
  { icon: 'message',             titleKey: 'pages.tools.howStepContinueTitle',  titleFallback: 'Continue in chat', copyKey: 'pages.tools.howStepContinueCopy',  copyFallback: 'Refine, remix, or build on your result in a fresh chat.' },
];

const createStyles = (colors, isXS, isMD, palette, insetBottom = 0, catalogPalette) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  },
  scroll: { flex: 1, width: '100%' },
  scrollContent: { width: '100%', paddingBottom: insetBottom + 80 },

  // ---------- Layout ----------
  inner: {
    padding: isXS ? 15 : 24,
    paddingTop: isXS ? 18 : 28,
    maxWidth: 1240,
    alignSelf: 'center',
    width: '100%',
  },
  // Each panel keeps its natural height. On small screens, stack panels
  // without flex:1 so the unbounded ScrollView cannot collapse either one.
  workArea: {
    flexDirection: isMD ? 'column' : 'row',
    gap: isMD ? 18 : 22,
    alignItems: 'flex-start',
  },
  column: {
    flex: isMD ? undefined : 1,
    minWidth: 0,
    gap: isXS ? 12 : 14,
    width: '100%',
  },
  workSection: {
    width: '100%',
  },

  // ---------- Section header (compact) ----------
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
    minHeight: 36,
  },
  sectionIcon: {
    width: 26,
    height: 26,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: catalogPalette.preview,
  },
  sectionTitle: {
    fontSize: isXS ? 16 : 17,
    letterSpacing: -0.1,
  },
  // Inline count chip rendered right next to the title (not pinned to the
  // right). Reads more like part of the heading than a stat in a corner.
  sectionCountPill: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: colors.solid_color_tertiary,
  },
  sectionHeaderSpacer: {
    flex: 1,
  },
  sectionHeaderAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 36,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    borderWidth: 0,
    borderColor: colors.border_color_secondary,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'all 0.15s' }),
  },
  sectionHeaderActionHover: {
    backgroundColor: colors.solid_color_tertiary,
    borderColor: colors.border_color_primary,
  },
  sectionHeaderActionDisabled: {
    opacity: 0.4,
    ...(Platform.OS === 'web' && { cursor: 'not-allowed' }),
  },
  sectionStatusInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: colors.solid_color_tertiary,
  },

  // Neutral surface keeps attention on the input and primary action.
  card: {
    padding: isXS ? 16 : 20,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: catalogPalette.border,
    backgroundColor: catalogPalette.surface,
  },
  resultStack: {
    gap: 12,
  },
  // Shown only in the gap between "Run" pressed and the HTTP response returning
  // a ToolRun id — keeps the result column open on mobile without flashing the
  // empty-state card while `activeRunId` is intentionally null.
  runStartingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_tertiary,
  },

  // Section spacing between Configure/Result row and Recent runs / Examples.
  sectionGap: {
    marginTop: isXS ? 22 : 28,
  },

  // ---------- How it works (compact, near bottom) ----------
  howWrap: {
    marginTop: isXS ? 28 : 36,
    paddingTop: isXS ? 22 : 28,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  howHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 10,
    marginBottom: 25,
  },
  howHeaderIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: catalogPalette.preview,
  },
  howHeaderTitle: {
    marginBottom: 0,
    lineHeight: 30,
  },
  howRow: {
    flexDirection: isXS ? 'column' : 'row',
    gap: 12,
    marginTop: 4,
  },
  howStep: {
    flex: isXS ? undefined : 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: 'transparent',
  },
  howIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: catalogPalette.preview,
  },

  // ---------- Page copy spacing ----------
  pageCopyWrap: {
    marginTop: isXS ? 28 : 40,
  },

  loading: {
    padding: 60,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },

  // ---------- Sticky native run CTA ----------
  // On iOS / Android the form often pushes the in-flow Run button below the
  // keyboard or far down a long scroll. We pin a slim CTA bar to the bottom
  // of the viewport so the primary action is always one tap away. Hidden
  // entirely on web / macOS / desktop layouts where the side-by-side
  // form+result panel already keeps Run in view.
  stickyBarWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: Math.max(10, insetBottom + 6),
    backgroundColor: colors.solid_color_block,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    gap: 8,
    // Sit above the result/related-tools content scrolling underneath.
    zIndex: 20,
  },
  stickyBarMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  stickyBarMetaLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    minWidth: 0,
  },
  stickyBarMetaCoin: {
    width: 14,
    height: 14,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_tertiary,
  },
});

// Coerces a raw URL string into the right JS type for a given field. URLs
// are always strings on the wire, so booleans/numbers/arrays need decoding.
function parseQueryValueForField(field, raw) {
  if (raw === undefined || raw === null) return undefined;
  const type = field?.type;
  if (type === 'boolean') return raw === 'true' || raw === '1' || raw === 'on';
  if (type === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  if (type === 'array') {
    return String(raw).split(',').map(s => s.trim()).filter(Boolean);
  }
  if (type === 'file') return undefined; // can't prefill a file from a URL
  return String(raw);
}

const ToolDetailPage = React.memo(({ slug, initialParams = null, querySignature = '' }) => {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS, isMD } = useResponsiveness();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { addToast } = useToastStore(s => s.actions);

  const liveTool = useToolStore(s => s.toolsBySlug[slug]);
  // Fall back to the build-time prerender cache so the page can render
  // proper meta tags / hero / page copy in the static HTML before the live
  // store fetch resolves on the client. Crawlers and slow networks both win.
  const staticTool = slug ? _staticToolCache[slug] : null;
  const tool = liveTool || staticTool || null;
  const isLoadingDetail = useToolStore(s => s.detailLoadingBySlug[slug]);
  const detailError = useToolStore(s => s.detailErrorBySlug[slug]);
  const fetchToolBySlug = useToolStore(s => s.fetchToolBySlug);

  const isRunning  = useToolStore(s => s.isRunningByToolSlug[slug]);
  const runError   = useToolStore(s => s.runErrorByToolSlug[slug]);
  const runTool    = useToolStore(s => s.runTool);
  const runs       = useToolStore(s => s.runsByToolSlug[slug]) || EMPTY_RUNS;
  const fetchRuns  = useToolStore(s => s.fetchRuns);
  const deleteRun  = useToolStore(s => s.deleteRun);

  // Cross-component bridge — fed by the navigation header dropdowns
  // (ToolPresetsDropdown / ToolRunsDropdown). The page reads these and
  // immediately clears them so each request fires exactly once.
  const pendingExample      = useToolStore(s => s.pendingExampleByToolSlug[slug]);
  const pendingActiveRun    = useToolStore(s => s.pendingActiveRunIdByToolSlug[slug]);
  const clearPendingExample = useToolStore(s => s.clearPendingExample);
  const clearPendingActiveRun = useToolStore(s => s.clearPendingActiveRun);

  const formRef = useRef(null);
  // Outer ScrollView ref + a measurable target on the Result section so we
  // can auto-scroll the user to the result the moment a run starts on
  // mobile — otherwise their first signal that anything happened is a tiny
  // status pill far below the form.
  const scrollRef = useRef(null);
  const resultAnchorRef = useRef(null);
  const [activeRunId, setActiveRunId] = useState(null);
  const [deletingRunId, setDeletingRunId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const catalogPalette = getCatalogPalette(theme);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  // Authoritative credit balance for the sticky native run CTA. We deliberately
  // don't try to mirror the form's debounced cost calc here — for the bottom
  // bar a "you have N credits, this run will roughly cost M" line is enough,
  // and it stays accurate after Run is tapped via the in-form footer anyway.
  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable) ?? 0;
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  const requireAuth = useRequireAuth();

  // Only show a result when the user has explicitly selected one (via run
  // submit, dropdown pick, or recent-runs strip click). We deliberately do
  // NOT auto-fall-back to runs[0] — landing on a tool page with a stale
  // result from yesterday is jarring; users should always start fresh.
  const activeRun = useMemo(
    () => runs.find(r => r.id === activeRunId) || null,
    [runs, activeRunId]
  );

  const pageCopy = useMemo(() => (
    Array.isArray(tool?.meta?.pageCopy)
      ? tool.meta.pageCopy.filter(block => (
        block?.title || block?.subtitle || block?.copy || (Array.isArray(block?.items) && block.items.length)
      ))
      : []
  ), [tool]);

  const palette = useMemo(() => getPaletteForTool(tool), [tool]);
  const visibleFields = useMemo(() => (
    (Array.isArray(tool?.fields) ? tool.fields : []).filter(f => f?.flags?.isVisibleToUsers !== false)
  ), [tool]);

  // Examples are normalized server-side against `fields` so we just need to
  // display them. We mirror the same filtering ToolForm uses so what the user
  // sees here matches what would actually be applied.
  const examples = useMemo(() => {
    const list = Array.isArray(tool?.examples) ? tool.examples : [];
    const fieldNames = new Set(visibleFields.map(f => f?.name).filter(Boolean));
    return list
      .filter(ex => ex && typeof ex === 'object' && !Array.isArray(ex))
      .map(ex => Object.fromEntries(
        Object.entries(ex).filter(([key, value]) => (
          fieldNames.has(key)
          && value !== undefined
          && value !== null
          && value !== ''
          && (!Array.isArray(value) || value.length > 0)
        ))
      ))
      .filter(ex => Object.keys(ex).length > 0);
  }, [tool, visibleFields]);

  const styles = useMemo(
    () => createStyles(colors, isXS, isMD, palette, insets.bottom, catalogPalette),
    [colors, isXS, isMD, palette, insets.bottom, catalogPalette]
  );

  // ---------- Handlers ----------

  useEffect(() => {
    if (!slug) return;
    // Always land on a clean slate — no stale result from a previous visit.
    setActiveRunId(null);
    setShowExamples(false);
    setShowAbout(false);
    fetchToolBySlug(slug).catch(() => {});
    // Runs are owner-only; skip for anonymous visitors to avoid a guaranteed
    // 401 and to keep the recent-runs strip hidden cleanly.
    if (hasUser) fetchRuns({ toolSlug: slug }).catch(() => {});

    // Fire-and-forget view beacon. Dedup'd at the module level so repeat
    // visits in the same session don't keep inflating tool.stats.views.
    if (!VIEWED_TOOL_SLUGS.has(slug)) {
      VIEWED_TOOL_SLUGS.add(slug);
      toolsApi.trackView(slug).catch(() => {});
    }
  }, [slug, fetchToolBySlug, fetchRuns, hasUser]);

  // (Per-run polling lives inside <ToolProgressIndicator />, which calls
  //  /json/tool-runs/:id/poll directly and writes results back into the
  //  global tool store. We deliberately don't re-list /json/tool-runs from
  //  here — that would double the number of requests in flight while a job
  //  is in progress.)

  const { openModal } = useModalStore(s => s.actions);
  const getModelById = useModelStore(s => s.getModelById);

  const handleSubmit = useCallback(async (inputs, opts = {}) => {
    if (!slug) return;
    // Running a tool spends credits and writes a run document — gate
    // anonymous visitors here so we don't burn a free credit pool or
    // surface a confusing 401 toast. Form state stays intact behind the
    // modal so they can resume after signing in.
    const ran = requireAuth(() => true, {
      source: 'tool_run',
      title: `Sign in to run ${tool?.name || 'this tool'}`,
      subtitle: 'Tool runs are tied to your account so we can save the output and apply your credits.',
      icon: 'play',
    });
    if (ran !== true) return;
    // Drop the prior result immediately so the user never sees stale output
    // stacked under a fresh "Running" state while the new /run round-trip is
    // in flight.
    setActiveRunId(null);
    // Dismiss the keyboard immediately on native — leaving it up while the
    // run kicks off covers half the Result column and the keyboard's return
    // key bounces back to whichever input was focused last, which feels like
    // the form rejected the submit. Cheaper than fighting focus state.
    if (IS_TOUCH_NATIVE) Keyboard.dismiss();
    try {
      const newRun = await runTool(slug, {
        inputs,
        modelId: opts.modelId || null,
        modelIds: Array.isArray(opts.modelIds) && opts.modelIds.length ? opts.modelIds : null,
        operation: opts.operation || null,
      });
      if (newRun?.id) setActiveRunId(newRun.id);
      if (newRun?.status === 'failed') {
        if (isPlusRequiredApiError(newRun?.error)) {
          const model = opts.modelId ? getModelById?.(opts.modelId) : null;
          openModelAccessPaywall(openModal, { model });
          return;
        }
        // The result card already renders the out-of-credits choices inline;
        // a red toast on top of it would just be noise.
        if (isInsufficientCreditsError(newRun?.error)) return;
        addToast(newRun?.error?.message || 'Tool run failed', 'error');
      }
    } catch (e) {
      if (isInsufficientCreditsError(e)) {
        useToolStore.setState((state) => ({
          runErrorByToolSlug: { ...state.runErrorByToolSlug, [slug]: null },
        }));
        openModal('OutOfCreditsModal', { source: 'tool_run' });
        return;
      }
      if (isPlusRequiredApiError(e)) {
        // Prefer the paywall over the red inline/toast error for this gate.
        useToolStore.setState((state) => ({
          runErrorByToolSlug: { ...state.runErrorByToolSlug, [slug]: null },
        }));
        const model = opts.modelId ? getModelById?.(opts.modelId) : null;
        openModelAccessPaywall(openModal, { model });
        return;
      }
      addToast(e?.message || 'Tool run failed', 'error');
    }
  }, [slug, runTool, addToast, requireAuth, tool?.name, openModal, getModelById]);

  // Pull-to-refresh on the detail page itself. The catalog and history both
  // already have it, and users on iOS / Android intuitively pull a stale
  // tool page to refetch the tool definition + their runs.
  const onRefresh = useCallback(async () => {
    if (!slug) return;
    setRefreshing(true);
    try {
      await Promise.allSettled([
        fetchToolBySlug(slug, { force: true }),
        hasUser ? fetchRuns({ toolSlug: slug, force: true }) : Promise.resolve(),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [slug, fetchToolBySlug, fetchRuns, hasUser]);

  // Mobile-only auto-scroll. When the result column is stacked under the form
  // and a new run starts, gently scroll the user down to the result anchor so
  // they don't think their tap did nothing. Desktop layouts already render
  // form + result side-by-side so they don't need this.
  const scrollToResult = useCallback(() => {
    if (!isMD || !scrollRef.current || !resultAnchorRef.current) return;
    // Fabric expects another mounted native-component ref here. Passing the
    // value returned by ScrollView#getInnerViewNode triggers React Native's
    // "measureLayout must be called with a ref" warning on iOS.
    try {
      resultAnchorRef.current.measureLayout?.(
        scrollRef.current,
        (_x, y) => {
          // Pull the result a hair above the very top of the viewport so the
          // section header is fully visible, not flush against the nav bar.
          scrollRef.current?.scrollTo?.({ y: Math.max(0, y - 12), animated: true });
        },
        () => scrollRef.current?.scrollTo?.({ y: 600, animated: true }),
      );
    } catch (e) {
      // measureLayout can throw if the node hasn't been laid out yet — falling
      // back to a generous scrollTo is still better than no scroll.
      scrollRef.current?.scrollTo?.({ y: 600, animated: true });
    }
  }, [isMD]);

  // Trigger the auto-scroll the first frame `isRunning` flips on or a fresh
  // `activeRunId` lands. We deliberately depend on isRunning AND activeRunId
  // so a "new run started" event fires either way (whichever the store
  // updates first). Web does not need this — it stays out of the dep chain.
  useEffect(() => {
    if (!IS_TOUCH_NATIVE) return;
    if (!isRunning && !activeRunId) return;
    // Defer one tick so the result column has had a chance to mount first
    // (the result is conditionally rendered when `isMD && showResultBlock`).
    const id = setTimeout(scrollToResult, 60);
    return () => clearTimeout(id);
  }, [isRunning, activeRunId, scrollToResult]);

  // Single source of truth for "load this run". Used by:
  //   - the Recent runs strip click handler
  //   - the pendingActiveRun bridge (header dropdown / cross-page nav)
  // Mirrors the ?runId= URL-param flow so the result panel AND the form fields
  // both reflect the chosen run. Without replaying `run.inputs` the form would
  // keep showing whatever the user last typed, which is confusing — clicking a
  // recent run means "I want to look at / tweak / re-run this", not just "swap
  // the result preview".
  const handleSelectRun = useCallback((run) => {
    if (!run?.id) return;
    setActiveRunId(run.id);
    if (run.inputs && typeof run.inputs === 'object') {
      formRef.current?.applyInputs?.(run.inputs);
    }
    const runModels = Array.isArray(run?.modelsUsed) ? run.modelsUsed : [];
    const runModelIds = runModels.map(m => m?.id || m).filter(Boolean);
    if (runModelIds.length > 1 && formRef.current?.setModelIds) {
      formRef.current.setModelIds(runModelIds);
    } else if (runModelIds.length) {
      formRef.current?.setModel?.(runModelIds[0]);
    }
  }, []);

  // Wipes the form back to defaults AND drops whatever result is currently on
  // screen — single button, single mental model: "start over".
  const handleResetTool = useCallback(() => {
    formRef.current?.reset?.();
    setActiveRunId(null);
  }, []);

  // Drain header-dropdown requests. The `_v` version counter on each pending
  // slot guarantees that picking the same preset / run twice in a row still
  // re-applies it (otherwise React would skip the effect on referential equality).
  useEffect(() => {
    if (!pendingExample) return;
    formRef.current?.applyExample?.(pendingExample.example, pendingExample.index);
    clearPendingExample(slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingExample?._v]);

  useEffect(() => {
    if (!pendingActiveRun) return;
    const runId = pendingActiveRun.runId;
    if (runId) {
      const run = runs.find(r => r.id === runId) || null;
      if (run) {
        handleSelectRun(run);
      } else {
        setActiveRunId(runId);
      }
    }
    clearPendingActiveRun(slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingActiveRun?._v]);

  // ---- URL-param prefill --------------------------------------------------
  // Supported query string params (everything else is treated as a value
  // override if it matches a tool field name):
  //   ?model=<modelSlug>     – pin a specific model (also accepts `?modelId=`)
  //   ?preset=<index>        – apply tool.examples[index] as a preset
  //   ?runId=<id>            – prefill the form (and result panel) from a run
  //   ?<fieldName>=<value>   – set the value of any visible tool field
  //
  // Order of resolution: preset → run → individual field overrides → model.
  // We deliberately re-run when the query string changes so deep links from
  // chat/share/bookmark always win over whatever was on screen.
  const lastAppliedQueryRef = useRef(null);
  useEffect(() => {
    if (!tool || !slug) return;
    if (!querySignature || querySignature === '{}') return;
    // Only run once per unique (tool, querystring) pair to avoid clobbering
    // user edits when the page re-renders for unrelated reasons.
    const key = `${tool.slug || tool.id}::${querySignature}`;
    if (lastAppliedQueryRef.current === key) return;
    lastAppliedQueryRef.current = key;

    const params = initialParams || {};
    const fieldByName = new Map(
      (Array.isArray(tool.fields) ? tool.fields : [])
        .filter(f => f?.name)
        .map(f => [f.name, f])
    );

    let cancelled = false;

    (async () => {
      // 1. Preset (lowest priority — just seeds the form before overrides)
      const presetRaw = params.preset ?? params.presetIndex;
      if (presetRaw != null && presetRaw !== '') {
        const idx = parseInt(presetRaw, 10);
        const ex  = Array.isArray(examples) ? examples[idx] : null;
        if (ex && Object.keys(ex).length) {
          formRef.current?.applyExample?.(ex, idx);
        }
      }

      // 2. RunId — switches the result panel + replays inputs into the form
      const runIdRaw = params.runId || params.historyId;
      if (runIdRaw) {
        setActiveRunId(runIdRaw);
        let run = runs.find(r => r.id === runIdRaw) || null;
        if (!run) {
          try {
            const data = await toolRunsApi.getById(runIdRaw);
            run = data?.toolRun || data || null;
          } catch (e) {
            // Non-fatal — the result panel will still try to load via the
            // active-run flow once the runs list refreshes.
          }
        }
        if (cancelled) return;
        if (run?.inputs && typeof run.inputs === 'object') {
          formRef.current?.applyInputs?.(run.inputs);
        }
        // If the caller didn't pin a model explicitly, mirror the run's
        // model(s). Multi-model runs replay the full pick set so the
        // "use this run" affordance recreates the same comparison.
        if (!params.model && !params.modelId) {
          const runModels = Array.isArray(run?.modelsUsed) ? run.modelsUsed : [];
          const ids = runModels.map(m => m?.id || m).filter(Boolean);
          if (ids.length > 1 && formRef.current?.setModelIds) {
            formRef.current.setModelIds(ids);
          } else if (ids.length) {
            formRef.current?.setModel?.(ids[0]);
          }
        }
      }

      // 3. Per-field overrides (highest priority — explicit beats implicit)
      const overrides = {};
      for (const [key, raw] of Object.entries(params)) {
        if (RESERVED_QUERY_KEYS.has(key)) continue;
        const field = fieldByName.get(key);
        if (!field) continue;
        const parsed = parseQueryValueForField(field, raw);
        if (parsed !== undefined) overrides[key] = parsed;
      }
      if (Object.keys(overrides).length) {
        formRef.current?.applyInputs?.(overrides);
      }

      // 4. Model — supports either ?model=<slug> or ?modelId=<id>. Slug is
      //    preferred because it's stable + readable in shared links.
      const modelRaw = params.model || params.modelId;
      if (modelRaw) {
        const ms = useModelStore.getState();
        let resolved = ms.getModelBySlug?.(modelRaw) || ms.getModelById?.(modelRaw) || null;
        if (!resolved && ms.fetchModelBySlug) {
          try { resolved = await ms.fetchModelBySlug(modelRaw); } catch (e) { /* ignore */ }
        }
        if (cancelled) return;
        if (resolved?.id) formRef.current?.setModel?.(resolved.id);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool?.slug, querySignature, runs.length]);

  const performDeleteRun = useCallback(async (run) => {
    if (!run?.id) return;
    setDeletingRunId(run.id);
    try {
      await deleteRun(run.id);
      if (run.id === activeRunId) setActiveRunId(null);
    } catch (e) {
      addToast(e?.message || 'Failed to delete run', 'error');
    } finally {
      setDeletingRunId(null);
    }
  }, [deleteRun, activeRunId, addToast]);

  const handleDeleteRun = useCallback((run) => {
    if (!run?.id) return;
    const toolPhrase = (run?.tool?.name || run?.toolSlug || tool?.name || '').trim()
      || (t('pages.tools.deleteRunUnknownTool') || 'this tool');
    const title = t('pages.tools.deleteRunTitle') || 'Delete this run?';
    const message = (t('pages.tools.deleteRunMessage')
      || 'This removes the run from your history for %TOOL%. You cannot undo this.')
      .replace('%TOOL%', toolPhrase);
    showConfirmationModal({
      title,
      message,
      confirmText: t('common.delete') || 'Delete',
      cancelText: t('common.cancel') || 'Cancel',
      isDangerous: true,
      options: {
        height: 180,
        width: 350,
      },
      onConfirm: () => {
        void performDeleteRun(run);
      },
    });
  }, [t, tool?.name, showConfirmationModal, performDeleteRun]);

  // ---------- Loading / error / not-found states ----------

  if (!tool && isLoadingDetail) {
    return <CatalogDetailPageSkeleton variant="tool" />;
  }

  if (!tool && detailError) {
    return (
      <View style={styles.container}>
        <View style={styles.inner}>
          <StatusContainer
            type='error'
            message={detailError}
            actionText={t('pages.common.retry') || 'Retry'}
            onAction={() => fetchToolBySlug(slug, { force: true })}
          />
        </View>
      </View>
    );
  }

  if (!tool) {
    return (
      <View style={styles.container}>
        <MetaHead
          title='Tool not found'
          description='This AI tool is no longer available. Browse the full catalog of Gab AI tools.'
          canonical={slug ? `/tools/${slug}` : '/tools'}
          noindex
        />
        <View style={styles.inner}>
          <StatusContainer
            type='empty'
            message={t('pages.tools.notFound') || 'Tool not found.'}
            actionText='Browse tools'
            onAction={() => router.push('/tools')}
          />
        </View>
      </View>
    );
  }

  const showResultBlock = !!(activeRun || isRunning);
  const activeStatus = activeRun
    ? getRunStatusVisual(activeRun.status, colors, palette, t)
    : (isRunning ? getRunStatusVisual('running', colors, palette, t) : null);

  // `showStickyRunBar` controls the floating bottom CTA. Once a result is on
  // screen the in-result action chips (Continue in chat / Download / etc.)
  // become the primary affordance, so we step out of the way.
  const showStickyRunBar = IS_TOUCH_NATIVE && isMD && !showResultBlock;
  // Reserve ~one CTA-bar height of extra scrollContent padding so the page's
  // last sections (Related tools / How it works) aren't trapped behind the
  // floating bar. The bar itself already paints its own safe-area inset, so
  // we just need its visual height here. The default scrollContent already
  // adds `insetBottom + 80` of slack — this stacks on top of that.
  const stickyBarReservedHeight = showStickyRunBar ? 96 : 0;

  // ---------- SEO ----------
  const seoTitle = tool.meta?.title || `${tool.name} — AI Tool`;
  const seoDescription = tool.meta?.description
    || tool.shortDescription
    || (tool.description && tool.description.length <= 280 ? tool.description : `Use ${tool.name} on Gab AI to ${tool.shortDescription || 'get instant AI-powered results'}.`);
  const seoCanonical = `/tools/${slug}`;
  // Prefer the generated 1200x630 social card; fall back to the square avatar.
  const seoImage = tool.meta?.ogImage || tool.avatarPath || undefined;
  const seoKeywords = tool.meta?.keywords
    || [tool.name, tool.category?.name, tool.outputType, 'AI tool', 'Gab AI'].filter(Boolean).join(', ');

  // SoftwareApplication + BreadcrumbList JSON-LD. SoftwareApplication is the
  // best-fit schema for a runnable AI utility — Google understands it and
  // can render rich results (rating, price, category) when present.
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        name: tool.name,
        description: seoDescription,
        url: `https://gab.ai${seoCanonical}`,
        applicationCategory: tool.category?.name || 'UtilitiesApplication',
        applicationSubCategory: tool.outputType || undefined,
        operatingSystem: 'Web, iOS, Android',
        ...(seoImage ? { image: seoImage } : {}),
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        provider: {
          '@type': 'Organization',
          name: 'Gab AI Inc.',
          url: 'https://gab.ai',
        },
        ...(tool.stats?.usageCount > 0 ? {
          interactionStatistic: {
            '@type': 'InteractionCounter',
            interactionType: 'https://schema.org/UseAction',
            userInteractionCount: tool.stats.usageCount,
          },
        } : {}),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gab.ai' },
          { '@type': 'ListItem', position: 2, name: 'AI Tools', item: 'https://gab.ai/tools' },
          { '@type': 'ListItem', position: 3, name: tool.name, item: `https://gab.ai${seoCanonical}` },
        ],
      },
    ],
  };

  return (
    <View style={styles.container}>
      <MetaHead
        title={seoTitle}
        description={seoDescription}
        canonical={seoCanonical}
        image={seoImage}
        keywords={seoKeywords}
        structuredData={seoStructuredData}
      />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        // iOS doesn't auto-resize the window when the keyboard appears, so
        // we lift the scroll view + sticky CTA above the keyboard with
        // `behavior='padding'`. Android already handles this via the manifest's
        // `windowSoftInputMode='adjustResize'`, and stacking another KAV on
        // top double-shrinks the layout — so we pass `undefined` there.
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? insets.top + 44 : 0}
      >
      <ScrollView
        ref={scrollRef}
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          // The base `scrollContent` already adds `insetBottom + 80` of slack
          // (see `createStyles`). We only stack the bar-specific reservation
          // on top of that — no need to re-add the safe-area inset here.
          showStickyRunBar && { paddingBottom: (insets.bottom || 0) + 80 + stickyBarReservedHeight },
        ]}
        showsVerticalScrollIndicator={false}
        // Touch-friendly: tapping a button inside a list while the keyboard
        // is up should fire that button (default RN behavior would dismiss
        // the keyboard and swallow the tap), and dragging the page closes
        // the keyboard naturally — both are baseline iOS expectations.
        keyboardShouldPersistTaps='handled'
        keyboardDismissMode={IS_TOUCH_NATIVE ? 'on-drag' : 'none'}
        refreshControl={IS_TOUCH_NATIVE ? (
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.text_color_secondary}
            colors={[colors.text_color_secondary]}
          />
        ) : undefined}
      >
        <View style={styles.inner}>
          <ToolDetailHeader tool={tool} compact={isXS} />
          <View style={styles.workArea}>
            {/* Main input and run action. */}
            <View style={styles.column}>
              <View style={styles.workSection}>
                <View style={styles.sectionHeader}>
                  <Text weight='bold' style={styles.sectionTitle}>
                    {t('pages.tools.yourInput') || 'Your input'}
                  </Text>
                  <View style={styles.sectionHeaderSpacer} />
                  {examples.length > 0 && <Pressable onPress={() => setShowExamples(value => !value)}
                    {...(Platform.OS === 'web' ? { 'aria-expanded': showExamples } : {})}
                    disabled={!!isRunning} accessibilityRole="button" accessibilityState={{ expanded: showExamples }}
                    style={styles.sectionHeaderAction}>
                    <Icon name="lightbulb" size={12} color={colors.text_color_secondary} />
                    <Text size="xs" color="secondary">{t('pages.tools.tryAnExample')}</Text>
                  </Pressable>}
                  <Pressable
                    onPress={handleResetTool}
                    disabled={!!isRunning}
                    style={({ hovered }) => [
                      styles.sectionHeaderAction,
                      hovered && Platform.OS === 'web' && styles.sectionHeaderActionHover,
                      isRunning && styles.sectionHeaderActionDisabled,
                    ]}
                    accessibilityRole='button'
                    accessibilityLabel={t('pages.tools.resetTool') || 'Reset'}
                  >
                    <Icon name='arrow-rotate-left' size={11} color={colors.text_color_secondary} />
                    <Text size='xs' weight='medium' color='secondary'>
                      {t('pages.tools.resetTool') || 'Reset'}
                    </Text>
                  </Pressable>
                </View>
                <View style={styles.card}>
                  <ToolForm
                    ref={formRef}
                    tool={tool}
                    isRunning={!!isRunning}
                    error={runError}
                    onSubmit={handleSubmit}
                    simplified
                    hideExamples={!showExamples}
                  />
                </View>
              </View>
            </View>

            {/* Keep the result alongside desktop inputs; on smaller screens,
                show it only after a run starts. */}
            {(!isMD || showResultBlock) && (
              <View ref={resultAnchorRef} collapsable={false} style={[styles.column, Platform.OS === 'web' && !isMD && { position: 'sticky', top: 20 }]}>
                <View style={styles.workSection}>
                  <View style={styles.sectionHeader}>
                    <Text weight='bold' style={styles.sectionTitle}>
                      {t('pages.tools.resultSection') || 'Result'}
                    </Text>
                    {showResultBlock && activeStatus && (
                      <View style={styles.sectionStatusInline}>
                        <Icon name={activeStatus.icon} size={9} color={activeStatus.color} solid />
                        <Text size='xxs' weight='bold' color='secondary'>{activeStatus.label}</Text>
                      </View>
                    )}
                  </View>

                  {showResultBlock ? (
                    <View style={styles.resultStack}>
                      {activeRun && <ToolProgressIndicator run={activeRun} />}
                      {isRunning && !activeRun && (
                        <View style={styles.runStartingRow}>
                          <ActivityIndicator size='small' color={colors.text_color_secondary} />
                          <Text size='s' color='secondary' weight='medium'>
                            {t('pages.tools.runningButton') || 'Running…'}
                          </Text>
                        </View>
                      )}
                      {activeRun && <ToolResultDisplay run={activeRun} />}
                    </View>
                  ) : (
                    <ToolEmptyResult tool={tool} />
                  )}
                </View>
              </View>
            )}
          </View>

          {/* ============ RECENT RUNS (horizontal strip) ============ */}
          {/* Owner-only — hidden entirely for anonymous visitors so the
              section header doesn't render above a perpetually empty strip. */}
          {hasUser && runs.length > 0 && (
          <View style={styles.sectionGap}>
            <View style={styles.sectionHeader}>
              <View style={styles.sectionIcon}>
                <Icon name='clock-rotate-left' size={13} color={palette.fg} />
              </View>
              <Text weight='bold' style={styles.sectionTitle}>
                {t('pages.tools.recentRuns') || 'Recent runs'}
              </Text>
              {runs.length > 0 && (
                <View style={styles.sectionCountPill}>
                  <Text size='xxs' weight='bold' color='secondary'>
                    {(runs.length === 1
                      ? (t('pages.tools.runsCountSingle') || '%COUNT% run')
                      : (t('pages.tools.runsCount')       || '%COUNT% runs')
                    ).replace('%COUNT%', runs.length)}
                  </Text>
                </View>
              )}
            </View>
            <ToolRecentRunsStrip
              runs={runs}
              activeRunId={activeRun?.id}
              onSelect={handleSelectRun}
              onDelete={handleDeleteRun}
              deletingRunId={deletingRunId}
              outputType={tool?.outputType}
              palette={palette}
            />
          </View>
          )}

          <View style={styles.howWrap}>
            <Pressable onPress={() => setShowAbout(value => !value)} accessibilityRole="button"
              {...(Platform.OS === 'web' ? { 'aria-expanded': showAbout } : {})}
              accessibilityState={{ expanded: showAbout }} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 44 }}>
              <Icon name="circle-info" size={15} color={colors.text_color_secondary} />
              <Text size="s" weight="semibold" style={{ flex: 1 }}>{t('pages.tools.aboutTool')}</Text>
              <Icon name={showAbout ? 'chevron-up' : 'chevron-down'} size={12} color={colors.text_color_secondary} />
            </Pressable>
          </View>
          {showAbout && <>
            {!!tool.description && <Text size="s" color="secondary" style={{ lineHeight: 22, marginTop: 12 }}>{tool.description}</Text>}
            {pageCopy.length > 0 && <View style={styles.pageCopyWrap}>
              <ToolPageCopy blocks={pageCopy} palette={palette} isMD={isMD} />
            </View>}
          {/* ============ HOW IT WORKS (compact, near bottom) ============ */}
          <View style={styles.howWrap}>
            <View style={styles.howHeader}>
              <View style={styles.howHeaderIcon}>
                <Icon name='circle-info' size={16} color={palette.fg} />
              </View>
              <Text size='xxxl' weight='bold' color='primary' style={styles.howHeaderTitle}>
                {t('pages.tools.howItWorks') || 'How it works'}
              </Text>
            </View>
            <View style={styles.howRow}>
              {HOW_IT_WORKS_STEPS.map((step) => {
                const title = t(step.titleKey) || step.titleFallback;
                const copy  = t(step.copyKey)  || step.copyFallback;
                return (
                  <View key={step.titleKey} style={styles.howStep}>
                    <View style={{ flex: 1, gap: 2 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 5, gap: 10 }}>
                        <View style={styles.howIcon}>
                          <Icon name={step.icon} size={14} color={palette.fg} />
                        </View>
                        <Text size='xl' weight='bold' color='primary'>{title}</Text>
                      </View>
                      <Text size='m' color='primary' style={{ lineHeight: 25 }}>{copy}</Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>

          </>}

          {showAbout && <>
          <View style={styles.sectionGap}><ToolRelatedTools slug={slug} /></View>
          {/* ============ RELATED RECIPES (PROMPTS) — last content section ============ */}
          <View style={[styles.sectionGap, { marginBottom: 0, paddingBottom: 0 }]}>
            <ToolRelatedPrompts slug={slug} />
          </View>

          </>}
          <AppDashboardFooterBottomBar metaText="Gab AI" termsLabel={t('common.termsOfService')}
            privacyLabel={t('common.privacyPolicy')} onNavigate={path => router.push(path)} />
        </View>
      </ScrollView>

      {/* ============ NATIVE STICKY RUN CTA ============
          A slim primary action bar pinned to the bottom of the viewport on
          phones / portrait tablets. Stays out of the way once a result is
          on screen so the result's own action chips can take over. */}
      {showStickyRunBar && (
        <View style={styles.stickyBarWrap} pointerEvents='box-none'>
          <View style={styles.stickyBarMetaRow}>
            <View style={styles.stickyBarMetaLeft}>
              <View style={styles.stickyBarMetaCoin}>
                <Icon name='coin' size={8} solid color={colors.text_color_secondary} />
              </View>
              <Text size='xs' color='secondary' weight='medium' numberOfLines={1}>
                {(t('pages.tools.balanceLabel') || 'Balance: %COUNT% credits')
                  .replace('%COUNT%', formatCreditBalance(totalAvailableCredits))}
              </Text>
            </View>
            {!!runs.length && (
              <Pressable
                onPress={() => router.push('/tools/history')}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole='button'
                accessibilityLabel={t('pages.tools.viewAllRuns') || 'View all runs'}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}
              >
                <Icon name='clock-rotate-left' size={10} color={colors.text_color_tertiary} />
                <Text size='xxs' color='tertiary' weight='medium'>
                  {runs.length === 1
                    ? (t('pages.tools.runsCountSingle') || '%COUNT% run').replace('%COUNT%', runs.length)
                    : (t('pages.tools.runsCount') || '%COUNT% runs').replace('%COUNT%', runs.length)}
                </Text>
              </Pressable>
            )}
          </View>
          <ToolRunButton
            variant="neutral"
            label={tool?.runButtonLabel || t('pages.tools.runButton') || 'Run'}
            loadingLabel={t('pages.tools.runningButton') || 'Running…'}
            icon={tool?.runButtonIcon || 'wand-magic-sparkles'}
            isLoading={!!isRunning}
            onPress={() => formRef.current?.submit?.()}
          />
        </View>
      )}
      </KeyboardAvoidingView>
    </View>
  );
});

ToolDetailPage.displayName = 'ToolDetailPage';

// Reserved URL params consumed directly by the page (model picker, preset
// resolver, run prefiller). Anything else with a matching tool field name
// is treated as a value override.
const RESERVED_QUERY_KEYS = new Set(['slug', 'model', 'modelId', 'preset', 'presetIndex', 'runId', 'historyId']);

export default function ToolDetailRoute() {
  const params = useLocalSearchParams();
  const rawSlug = params?.slug;
  const slug = Array.isArray(rawSlug) ? rawSlug[0] : rawSlug;

  // IMPORTANT: keep this useMemo above any conditional return so the hook
  // count is stable across renders. Navigating between a real slug and
  // /tools/index would otherwise change the hook count and trip React's
  // "Rendered fewer hooks than expected" runtime error.
  const { initialParams, querySignature } = useMemo(() => {
    const out = {};
    for (const [key, value] of Object.entries(params || {})) {
      if (key === 'slug') continue;
      out[key] = Array.isArray(value) ? value[0] : value;
    }
    return {
      initialParams: out,
      querySignature: JSON.stringify(out),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params || {})]);

  if (slug === 'index') {
    return <Redirect href="/tools" />;
  }

  return (
    <ToolDetailPage
      slug={slug}
      initialParams={initialParams}
      querySignature={querySignature}
    />
  );
}
